import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { formatMoney, formatNumber } from '../../Shared/Formatters';
import { useApexTheme } from './useApexTheme';

/**
 * Horizontal bar chart for ranked_list. Driven by real resolver data, so
 * tooltips and labels match the underlying numbers exactly.
 */
export default function BarChartBlock({ title, data, valueFormat, dir = 'ltr' }) {
    const palette = useApexTheme();
    const items = Array.isArray(data) ? data : [];
    const isMoney = valueFormat !== 'integer';

    const { categories, values } = useMemo(() => ({
        categories: items.map(d => d.name || '—'),
        values:     items.map(d => Number(d.value) || 0),
    }), [items]);

    if (!values.length) return null;

    const options = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui',
            background: 'transparent',
            animations: { enabled: true, speed: 400 },
            rtl: dir === 'rtl',
        },
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 4,
                barHeight: '70%',
                distributed: true,
                dataLabels: { position: 'top' },
            },
        },
        dataLabels: {
            enabled: true,
            textAnchor: 'start',
            formatter: (val) => (isMoney ? formatMoney(val) : formatNumber(val, 0)),
            offsetX: 6,
            style: { fontSize: '10px', colors: ['var(--v3-text-secondary)'], fontWeight: 600 },
        },
        colors: palette,
        legend: { show: false },
        xaxis: {
            categories,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { fontSize: '11px', colors: 'var(--v3-text-secondary)' },
                maxWidth: 110,
            },
        },
        grid: { show: false, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
        tooltip: {
            theme: 'dark',
            y: { formatter: (val) => (isMoney ? formatMoney(val) : formatNumber(val, 0)) },
        },
    };

    const height = Math.max(120, items.length * 28 + 20);

    return (
        <div className="chat-block">
            {title && <div className="chat-block-title">{title}</div>}
            <Chart type="bar" options={options} series={[{ name: '', data: values }]} height={height} />
        </div>
    );
}
